'use client';

import {
  RegularFacet as HeadlessRegularFacet,
  RegularFacetState,
  RegularFacetValue,
} from '@coveo/headless-react/ssr-commerce';
import { Checkbox, FilterGroup } from '@greystar/designsystem';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { GetHighlightsMapping } from '@greystar/lib';

interface IRegularFacetProps {
  controller?: HeadlessRegularFacet;
  staticState: RegularFacetState;
  onSelectionChange: (value: string) => void;
  selections?: Set<string>;
  analytics?: {
    trackEvent: <T extends Record<string, unknown>>(event: string, data: T) => void;
  };
}

export interface ClearRegularFacetRef {
  onClickClearSelectedFacetValues: () => void;
  isLoading: boolean;
}

const RegularFacet = forwardRef<ClearRegularFacetRef, IRegularFacetProps>((props, ref) => {
  const { controller, staticState, onSelectionChange, selections = new Set(), analytics } = props;
  const [state, setState] = useState(staticState);
  const HIGHLIGHTS_MAPPING = GetHighlightsMapping();

  useEffect(() => {
    controller?.subscribe(() => setState(controller.state));
  }, [controller]);

  const onClickClearSelectedFacetValues = (): void => {
    controller?.deselectAll();
    analytics?.trackEvent('filters_clear', {
      action_type: `${getFilterType(state.facetId)} filters - clear`,
    });
  };

  const isLoading = state.isLoading;

  useImperativeHandle(ref, () => ({
    onClickClearSelectedFacetValues,
    isLoading,
  }));

  const getFilterType = (facetId: string): string => {
    const filterTypes: Record<string, string> = {
      bedrooms_string: 'bedrooms',
      bathrooms_string: 'bathrooms',
      building_type: 'property type',
      highlights: 'features',
    };
    return filterTypes[facetId] || facetId;
  };

  const generateLabels = (facetId: string, value: RegularFacetValue) => {
    if (facetId === 'bedrooms_string') {
      return (
        {
          '0': 'Studio',
          '1': '1 Bedroom',
          '2': '2 Bedrooms',
          '3': '3+ Bedrooms',
        }[value.value] || ''
      );
    }
    if (facetId === 'bathrooms_string') {
      return (
        {
          '0': 'No Bathroom',
          '1': '1 Bathroom',
          '2': '2 Bathrooms',
          '3': '3+ Bathrooms',
        }[value.value] || ''
      );
    }
    if (facetId === 'highlights') {
      return (
        HIGHLIGHTS_MAPPING[value.value as keyof typeof HIGHLIGHTS_MAPPING]?.text ?? value.value
      );
    }
    return value.value;
  };

  const renderFacetValues = () => {
    return (
      <div className="FacetValuesControls">
        <div className="checkbox-filter-type">
          <div className="checkbox-filter-type--wrapper">
            {state.values
              .filter((value) => {
                if (state.facetId === 'bedrooms_string') {
                  const num = parseInt(value.value);
                  return num <= 3 || isNaN(num);
                }
                return true;
              })
              .map((value) => {
                const label = generateLabels(state.facetId, value);
                if (!label) return null;

                const isSelected = selections.has(value.value) || value.state !== 'idle';

                return (
                  <div key={value.value} className="checkbox-filter-type--filter">
                    <Checkbox
                      inputProps={{
                        disabled: isLoading,
                        onChange: () => {
                          const action = isSelected ? 'deselected' : 'selected';
                          analytics?.trackEvent('filters_selection', {
                            action_type: `${getFilterType(state.facetId)} - ${action}`,
                            action_value: value.value,
                          });
                          onSelectionChange(value.value);
                        },
                        checked: isSelected,
                      }}
                      value=""
                      label={label}
                      id={`${state.facetId}-${value.value}`}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <FilterGroup key={state.displayName ?? state.facetId}>
      <FilterGroup.Header title={state.displayName ?? state.facetId} />
      <FilterGroup.Body>{renderFacetValues()}</FilterGroup.Body>
    </FilterGroup>
  );
});

RegularFacet.displayName = 'RegularFacet';
export default RegularFacet;
